import { applyQueryConfig, ServiceHandler } from '../../utils';
import { accountServiceMappings } from '../../accounts/accountServiceMappings';

/**
 * One location record for an agency, from fir-agency's `locations` index. This is
 * the only source of an agency's street address and geo point — policy, binder and
 * quote records carry `agencyId` and the agent's contact details, but no address.
 */
export interface AgencyLocationDto {
  readonly id: string;
  readonly agencyId: string;
  readonly name?: string;
  readonly address1?: string;
  readonly address2?: string;
  readonly city?: string;
  readonly state?: string;
  readonly zipCode?: string;
  readonly country?: string;
  readonly phoneNumber?: string;
  readonly businessEmail?: string;
  readonly isHeadquarters?: boolean;
  readonly location?: { readonly lat: number; readonly lon: number };
}

interface AgencyLocationPage {
  readonly content: AgencyLocationDto[];
}

export interface AgencyLocationSearchParams {
  readonly location?: string;
  readonly name?: string;
  readonly agencyId?: string;
  /** Free text, matched across name, city, state, ZIP and street. */
  readonly search?: string;
  /** Centre of a geo filter, with `dist` as its radius (e.g. `50mi`). */
  readonly lat?: number;
  readonly lon?: number;
  readonly dist?: string;
  readonly page?: number;
  /** Named `size` because that is the key `accountServiceMappings` emits. */
  readonly size?: number;
}

/**
 * `applyQueryConfig` builds its output solely from the mapping array, so any key
 * absent from the map is dropped rather than passed through. `accountServiceMappings`
 * has no `agencyId` entry, which would silently turn a by-id lookup into an
 * unfiltered search — and resolve the wrong agency's address. Extending a local copy
 * keeps the shared map untouched for its other consumers.
 */
const agencyLocationMappings = [
  ...accountServiceMappings,
  { path: 'agencyId' },
  // The directory can filter by distance itself. Without these the radius could
  // only be applied to whichever page came back, so an agency one ZIP away was
  // missing from a "within 50 miles" search altogether.
  { path: 'lat' },
  { path: 'lon' },
  { path: 'dist' },
];

const agencyLocationSearchHandler = ServiceHandler<unknown, AgencyLocationPage>(
  {
    url: '/api/agency/v1/location/agency/search',
  }
);

/** Free-text agency search — backs the Find an Agent map and result list. */
export function searchAgencyLocations(params: AgencyLocationSearchParams = {}) {
  return agencyLocationSearchHandler({
    params: applyQueryConfig(agencyLocationMappings, params),
  });
}

/**
 * Every location on one agency — resolves an address from a policy's `agencyId`.
 *
 * The results are filtered here as well as requested by `agencyId`, because the
 * directory ignores that term: its `query()` passes a null agencyId to the query
 * builder, so an unfiltered page comes back and the caller would otherwise show
 * a different agency's address as the policyholder's own.
 */
export async function getAgencyLocations(params: {
  readonly agencyId: string;
}) {
  const response = await searchAgencyLocations({
    ...params,
    page: 0,
    size: 25,
  });
  const content = (response?.data?.content ?? []).filter(
    (location) => location.agencyId === params.agencyId
  );
  return { ...response, data: { ...response.data, content } };
}
